import React from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import MainNavigation from '../../components/ui/MainNavigation';
import SearchBar from '../../components/ui/SearchBar';
import HeroSection from './components/HeroSection';
import FeaturedTrailers from './components/FeaturedTrailers';
import HowItWorks from './components/HowItWorks';
import WhyChooseUs from './components/WhyChooseUs';
import TrailerCategories from './components/TrailerCategories';
import Testimonials from './components/Testimonials';
import CallToAction from './components/CallToAction';
import Footer from './components/Footer';

const HomePage = () => {
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();

  const handleSearch = (searchParams) => {
    navigate('/search-results', { state: { searchData: searchParams } });
  };

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "Draggin Trailers",
    "url": "https://draggintrailers.com",
    "description": "Rent trailers from trusted local owners. Browse cargo, utility, enclosed, and specialty trailers for your hauling needs.",
    "potentialAction": {
      "@type": "SearchAction",
      "target": "https://draggintrailers.com/search?q={search_term_string}",
      "query-input": "required name=search_term_string"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Draggin Trailers",
      "logo": {
        "@type": "ImageObject",
        "url": "https://draggintrailers.com/logo.png"
      },
      "sameAs": [
        "https://www.facebook.com/draggintrailers",
        "https://www.instagram.com/draggintrailers"
      ]
    }
  };

  const localBusinessData = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": "Draggin Trailers",
    "description": "Peer-to-peer trailer rental marketplace connecting trailer owners with renters nationwide.",
    "url": "https://draggintrailers.com",
    "priceRange": "$$",
    "address": {
      "@type": "PostalAddress",
      "addressCountry": "US"
    },
    "sameAs": [
      "https://www.facebook.com/draggintrailers",
      "https://www.instagram.com/draggintrailers"
    ]
  };

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Draggin Trailers - Rent Trailers from Local Owners | Cargo, Utility & More</title>
        <meta name="description" content="Find and rent trailers near you. Browse cargo trailers, utility trailers, enclosed trailers, and specialty equipment from verified local owners. Affordable daily and weekly rates." />
        <meta name="keywords" content="trailer rental, rent trailer, cargo trailer, utility trailer, enclosed trailer, equipment rental, peer-to-peer rental" />
        <link rel="canonical" href="https://draggintrailers.com" />
        
        {/* Open Graph Tags */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Draggin Trailers - Rent Trailers from Local Owners" />
        <meta property="og:description" content="Find and rent trailers near you. Browse cargo, utility, enclosed, and specialty trailers from verified local owners. Affordable rates and instant booking." />
        <meta property="og:url" content="https://draggintrailers.com" />
        <meta property="og:site_name" content="Draggin Trailers" />
        <meta property="og:image" content="https://draggintrailers.com/og-image.jpg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:locale" content="en_US" />
        
        {/* Twitter Card Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Draggin Trailers - Rent Trailers from Local Owners" />
        <meta name="twitter:description" content="Find and rent trailers near you. Browse cargo, utility, enclosed, and specialty trailers from verified local owners." />
        <meta name="twitter:image" content="https://draggintrailers.com/twitter-image.jpg" />
        
        {/* JSON-LD Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(localBusinessData)}
        </script>
      </Helmet>

      <MainNavigation isAuthenticated={isAuthenticated} user={user} />
      
      <div className="pt-20">
        <HeroSection />

        <section className="py-8 md:py-12 lg:py-16 bg-background">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <SearchBar onSearch={handleSearch} />
          </div>
        </section>

        <FeaturedTrailers />
        <HowItWorks />
        <WhyChooseUs />
        <TrailerCategories />
        <Testimonials />
        <CallToAction />
        <Footer />
      </div>
    </div>
  );
};

export default HomePage;